import { ADMIN_ROLES, ANNOUNCEMENT_ACTION_TYPES, ANNOUNCEMENT_DISPLAY_RULES, ANNOUNCEMENT_PLACEMENTS, ANNOUNCEMENT_STATUSES } from '../types/admin'

const INTERNAL_ROUTE = /^\/(?!\/)[A-Za-z0-9/_?=&.%#:+-]*$/
const SUPPORTED_USER_ROUTES = new Set(['', 'home', 'notice', 'notices', 'timetable', 'campusmap', 'map', 'more', 'settings', 'chatbot', 'sugi-instant', 'report', 'app-guide', 'cafeteria', 'shuttle', 'credit', 'profile-management', 'data-management'])

export function isValidInternalRoute(value) {
  const route = String(value ?? '').trim()
  if (!INTERNAL_ROUTE.test(route)) return false
  const root = route.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '').split('/')[0]
  return SUPPORTED_USER_ROUTES.has(root)
}

export function isValidExternalUrl(value) {
  try {
    const url = new URL(String(value ?? '').trim())
    return url.protocol === 'https:'
  } catch {
    return false
  }
}

export function validateAnnouncement(input, { forPublish = false } = {}) {
  const errors = {}
  if (!String(input?.title ?? '').trim()) errors.title = '제목을 입력하세요.'
  if (!String(input?.description ?? '').trim()) errors.description = '설명을 입력하세요.'
  if (!ANNOUNCEMENT_STATUSES.includes(input?.status)) errors.status = '올바른 상태를 선택하세요.'
  if (!ANNOUNCEMENT_PLACEMENTS.includes(input?.placement)) errors.placement = '올바른 노출 위치를 선택하세요.'
  if (!ANNOUNCEMENT_ACTION_TYPES.includes(input?.actionType)) errors.actionType = '올바른 CTA 동작을 선택하세요.'
  if (!ANNOUNCEMENT_DISPLAY_RULES.includes(input?.displayRule)) errors.displayRule = '올바른 노출 규칙을 선택하세요.'
  if (!Array.isArray(input?.targetPlatforms) || input.targetPlatforms.length === 0) errors.targetPlatforms = '대상 플랫폼을 하나 이상 선택하세요.'
  if (!Number.isInteger(Number(input?.priority)) || Number(input.priority) < 0 || Number(input.priority) > 999) errors.priority = '우선순위는 0~999 정수여야 합니다.'
  if (input?.actionType === 'route' && !isValidInternalRoute(input?.actionTarget)) errors.actionTarget = '앱 내부 경로는 /로 시작해야 합니다.'
  if (input?.actionType === 'externalUrl' && !isValidExternalUrl(input?.actionTarget)) errors.actionTarget = '외부 URL은 https:// 주소여야 합니다.'
  if (input?.actionType !== 'dismiss' && !String(input?.actionLabel ?? '').trim()) errors.actionLabel = 'CTA 문구를 입력하세요.'
  // saveAnnouncement()는 저장 직전 빈 문자열을 null로 정규화하므로(이미지 없음), 여기서도 빈 값은
  // 통과시키고 값이 있을 때만 firestore.rules의 imageUrl 조건(https://, 2048자 이하)과 동일하게 검증한다.
  // 클라이언트에서 걸러내지 않으면 잘못된 값이 저장 시점에야 일반 Firestore 권한 오류로만 드러난다.
  const imageUrl = String(input?.imageUrl ?? '').trim()
  if (imageUrl && (imageUrl.length > 2048 || !isValidExternalUrl(imageUrl))) errors.imageUrl = '이미지 URL은 https://로 시작하는 2048자 이하 주소여야 합니다.'
  const start = input?.startAt ? new Date(input.startAt) : null
  const end = input?.endAt ? new Date(input.endAt) : null
  if (start && Number.isNaN(start.getTime())) errors.startAt = '시작 시간을 확인하세요.'
  if (end && Number.isNaN(end.getTime())) errors.endAt = '종료 시간을 확인하세요.'
  if (start && end && end <= start) errors.endAt = '종료 시간은 시작 시간보다 늦어야 합니다.'
  if (forPublish && input?.status === 'scheduled' && !start) errors.startAt = '예약 게시에는 시작 시간이 필요합니다.'
  return { valid: Object.keys(errors).length === 0, errors }
}

export function findPriorityConflicts(candidate, announcements, now = new Date()) {
  if (!['scheduled', 'published'].includes(candidate?.status)) return []
  return (announcements ?? []).filter(item => {
    if (!item || item.id === candidate.id || item.status !== 'published') return false
    const end = item.endAt?.toDate?.() ?? (item.endAt ? new Date(item.endAt) : null)
    if (end && end <= now) return false
    const samePlacement = item.placement === candidate.placement
    const sharedPlatform = (item.targetPlatforms ?? []).some(platform => candidate.targetPlatforms?.includes(platform))
    return samePlacement && sharedPlatform && Number(item.priority ?? 0) >= Number(candidate.priority ?? 0)
  })
}

export function normalizeReportStatus(status) {
  return status === 'pending' ? 'new' : status
}

export function isValidAdminRole(value) {
  return ADMIN_ROLES.includes(value)
}

export function isValidAdminDisplayName(value) {
  const trimmed = String(value ?? '').trim()
  return trimmed.length > 0 && trimmed.length <= 80
}

export function validateAdminRoleAssignment(input) {
  const errors = {}
  if (!/^[A-Za-z0-9:_-]{3,200}$/.test(String(input?.targetUid ?? ''))) errors.targetUid = '올바른 관리자 UID가 아닙니다.'
  if (!isValidAdminRole(input?.role)) errors.role = '올바른 역할을 선택하세요.'
  if (!isValidAdminDisplayName(input?.displayName)) errors.displayName = '표시 이름을 입력하세요(80자 이하).'
  return { valid: Object.keys(errors).length === 0, errors }
}
